import React, { useState, useEffect, useMemo } from 'react';
import { universityService } from '../../services/universityService';
import { University } from '../../types';
import { useRouter, Link } from '../../router/Router';
import { useAuth } from '../../auth/AuthContext';
import { DemandDataEntryModal } from '../admin/demand/DemandDataEntryModal';
import {
  Search,
  Building2,
  MapPin,
  GraduationCap,
  ArrowRight,
  Filter,
  Sparkles,
  CalendarDays,
  CheckCircle2,
} from 'lucide-react';

export const UniversitySearch: React.FC = () => {
  const { navigate } = useRouter();
  const { studentProfile } = useAuth();
  const [universities, setUniversities] = useState<University[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGovernorate, setSelectedGovernorate] = useState<string>('ALL');
  const [isDemandModalOpen, setIsDemandModalOpen] = useState(false);
  const [demandSuccessNotice, setDemandSuccessNotice] = useState<string | null>(null);

  useEffect(() => {
    const loadUniversities = async () => {
      setIsLoading(true);
      const data = await universityService.getActiveUniversities();
      setUniversities(data);
      setIsLoading(false);
    };

    loadUniversities();
  }, []);

  const governorates = useMemo(() => {
    const set = new Set<string>();
    universities.forEach((u) => {
      if (u.governorate) set.add(u.governorate);
    });
    return Array.from(set);
  }, [universities]);

  const filteredUniversities = useMemo(() => {
    return universities.filter((u) => {
      const matchQuery =
        !searchQuery.trim() ||
        u.name.toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
        (u.governorate && u.governorate.toLowerCase().includes(searchQuery.trim().toLowerCase())) ||
        (u.city && u.city.toLowerCase().includes(searchQuery.trim().toLowerCase()));

      const matchGov = selectedGovernorate === 'ALL' || u.governorate === selectedGovernorate;

      return matchQuery && matchGov;
    });
  }, [universities, searchQuery, selectedGovernorate]);

  return (
    <div className="booking-page-container" dir="rtl">
      {/* Success Notification */}
      {demandSuccessNotice && (
        <div className="alert-success-banner" style={{ marginBottom: '1.5rem' }}>
          <CheckCircle2 size={20} className="text-emerald-600" />
          <span>{demandSuccessNotice}</span>
        </div>
      )}

      {/* Page Header */}
      <div className="booking-page-header">
        <div className="header-breadcrumbs">
          <Link to="/student" className="breadcrumb-link">
            لوحة الطالب
          </Link>
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-current">اختيار الجامعة</span>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="page-title">اختر جامعتك لاستعراض الرحلات المتاحة</h1>
            <p className="page-subtitle">
              رحلات مباشرة من الإسكندرية لجميع الجامعات المصرية بحافلات سياحية مجهزة (50 راكباً)
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsDemandModalOpen(true)}
            className="btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem' }}
          >
            <CalendarDays size={18} />
            <span>طلب موعد لجامعة غير مدرجة</span>
          </button>
        </div>
      </div>

      {/* Search & Filter Controls */}
      <div className="search-filter-card">
        <div className="search-input-wrapper">
          <Search className="search-icon" size={20} />
          <input
            type="text"
            className="search-input"
            placeholder="ابحث باسم الجامعة أو المحافظة (مثال: جامعة القاهرة، طنطا، عين شمس...)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-search-btn" onClick={() => setSearchQuery('')}>
              ×
            </button>
          )}
        </div>

        {/* Governorate Pills */}
        <div className="gov-pills-row">
          <span className="filter-label">
            <Filter size={14} /> تصفية بالمحافظة:
          </span>
          <button
            className={`gov-pill ${selectedGovernorate === 'ALL' ? 'active' : ''}`}
            onClick={() => setSelectedGovernorate('ALL')}
          >
            الكل ({universities.length})
          </button>
          {governorates.map((gov) => {
            const count = universities.filter((u) => u.governorate === gov).length;
            return (
              <button
                key={gov}
                className={`gov-pill ${selectedGovernorate === gov ? 'active' : ''}`}
                onClick={() => setSelectedGovernorate(gov)}
              >
                {gov} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Content Area */}
      {isLoading ? (
        <div className="loading-state-card">
          <div className="loading-spinner"></div>
          <p>جاري تحميل الجامعات المعتمدة...</p>
        </div>
      ) : filteredUniversities.length === 0 ? (
        <div className="empty-state-card">
          <Building2 size={48} className="empty-state-icon" />
          <h3>لم نجد أي جامعة تطابق بحثك</h3>
          <p>
            تأكد من كتابة الاسم بصيغة صحيحة، أو يمكنك تسجيل طلب جديد لجامعتك لتوفير باص 50 راكب فور اكتمال الأعداد.
          </p>
          <div className="empty-state-actions">
            <button
              className="btn-secondary"
              onClick={() => {
                setSearchQuery('');
                setSelectedGovernorate('ALL');
              }}
            >
              عرض جميع الجامعات
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setIsDemandModalOpen(true)}
            >
              <CalendarDays size={18} />
              <span>طلب موعد لجامعة غير مدرجة</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="universities-grid">
          {filteredUniversities.map((uni) => (
            <div key={uni.id} className="university-card">
              <div className="uni-card-header">
                <div className="uni-icon-box">
                  <GraduationCap size={24} />
                </div>
                <div className="uni-meta">
                  <span className="uni-gov-tag">
                    <MapPin size={12} /> {uni.governorate}
                  </span>
                  <h3 className="uni-name">{uni.name}</h3>
                </div>
              </div>

              <div className="uni-card-body">
                <p className="uni-campus-text">
                  <strong>الحرم الجامعي:</strong> {uni.campus || uni.city || 'المقر الرئيسي'}
                </p>
                <div className="uni-feature-pill">
                  <Sparkles size={14} />
                  <span>رحلات يومية مباشرة من محطات الإسكندرية</span>
                </div>
              </div>

              <div className="uni-card-footer">
                <button
                  className="btn-select-uni"
                  onClick={() => navigate(`/student/trips?universityId=${uni.id}`)}
                >
                  <span>عرض الرحلات المتاحة</span>
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Demand Data Entry Modal - Same as Admin Demand Entry */}
      <DemandDataEntryModal
        isOpen={isDemandModalOpen}
        onClose={() => setIsDemandModalOpen(false)}
        initialStudentName={studentProfile?.fullName}
        initialPhone={studentProfile?.phoneNumber}
        initialUniversityId={studentProfile?.universityId}
        initialUniversityName={searchQuery.trim()}
        onRecordCreated={(newRec) => {
          setDemandSuccessNotice(
            `تم تسجيل طلبك لجامعة (${newRec.universityNameSnapshot}) بنجاح! تم حفظ الطلب في سجلات وإحصائيات قسم الـ Demand.`
          );
          setTimeout(() => setDemandSuccessNotice(null), 8000);
        }}
      />
    </div>
  );
};
